import React, { type HTMLAttributes } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Container, Icon, WysiwygField } from '@uncinc/uncinc-react-kitchen-sink';
import { BreadcrumbsV2, DynamicMediaItem } from '@uncinc/react-drupal-core';
import type { ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { dateFormatter } from '../../../../helpers';
import { translateEmploymentTypes } from '../../../../helpers/translatedEmploymentTypes';

import type { NodeVacancy } from '../../../../types/uncinc_profile/nodes';
import {
  ButtonWrapper,
  ParagraphsRenderer,
  Tags,
} from '../../../../components';
import { taxonomyTagEntityMapper } from '../../../../components/Tags/taxonomyTagMapper';

import { APP_OPTIONS, DEFAULT_LANGUAGE } from '../../../../config/constants';

import './style.scss';

interface NodeVacancyProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<NodeVacancy>;
}

const NodeVacancyPage: React.FC<NodeVacancyProps> = ({ entity, className }) => {
  const { t } = useTranslation();
  const classes = classNames('Node', 'NodeVacancyPage', className);

  const vacancy = entity.toJS();
  const title = vacancy.title[0].value;
  const paragraphs = vacancy.field_paragraphs;
  const body = vacancy.body[0]?.processed;
  const lang = vacancy.langcode[0]?.value || DEFAULT_LANGUAGE;
  const date = vacancy.field_publish_date[0]?.value || vacancy.created[0]?.value;

  const image = vacancy.field_media_item[0];

  const locations = vacancy.field_locations;
  const streetAddress = [
    locations[0]?.entity.field_address[0]?.address_line1,
    locations[0]?.entity.field_address[0]?.address_line2,
    locations[0]?.entity.field_address[0]?.address_line3,
  ].join(' ');

  const themes = vacancy.field_themes;

  const htmlTitle = title.replace(/ /g, '%20');
  const {
    vacancyCompanyName,
    vacancyCompanyCOC,
    vacancyCompanyURL,
    vacancyCompanyLogo,
    vacancyEmailAddress,
    vacancyEmailSubject,
    vacancyEmailBody,
  } = APP_OPTIONS.parameters;

  const employmentTypes = vacancy.field_employment_types;
  const translatedEmploymentTypes = translateEmploymentTypes(
    vacancy.field_employment_types,
    lang,
  );

  const hoursMinimum = vacancy.field_hours_minimum[0]?.value;
  const hoursMaximum = vacancy.field_hours_maximum[0]?.value;
  const salaryMinimum = vacancy.field_salary_minimum[0];
  const salaryMaximum = vacancy.field_salary_maximum[0];
  const validThrough = vacancy.field_valid_through;
  const salaryType = vacancy.field_salary_type[0].value;

  const benefits = vacancy.field_benefits;
  const requirements = vacancy.field_requirements;

  const schema = JSON.stringify({
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title,
    description: body,
    identifier: {
      '@type': 'PropertyValue',
      name: vacancyCompanyName,
      value: vacancyCompanyCOC,
    },
    datePosted: date,
    validThrough: validThrough[0]?.value,
    employmentType: employmentTypes.map((type) => type.value),
    hiringOrganization: {
      '@type': 'Organization',
      name: vacancyCompanyName,
      sameAs: vacancyCompanyURL,
      logo: vacancyCompanyLogo,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress,
        postalCode: locations[0]?.entity.field_address[0]?.postal_code,
        addressCountry: locations[0]?.entity.field_address[0]?.country_code,
      },
    },
    baseSalary: {
      '@type': 'MonetaryAmount',
      currency: salaryMaximum.currency_code,
      value: {
        '@type': 'QuantitativeValue',
        minValue: salaryMinimum?.number,
        maxValue: salaryMaximum.number,
        unitText: salaryType,
      },
    },
  });

  return (
    <>
      <script type="application/ld+json">
        {schema}
      </script>

      <article itemType="http://schema.org/WebPage" className={classes}>
        <Container>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <BreadcrumbsV2 />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <div className="NodeVacancyPage__hero">
                <div className="NodeVacancyPage__hero__meta">
                  {themes.length > 0 && (
                    <span className="NodeVacancyPage__hero__meta__themes">
                      <Tags items={taxonomyTagEntityMapper(themes)} />
                    </span>
                  )}
                  <span>{dateFormatter(date, lang)}</span>
                </div>
                <div className="NodeVacancyPage__hero__text">
                  <h1 itemProp="name">{title}</h1>
                  {body && (
                    <WysiwygField
                      className="body--large"
                      property="schema:text"
                      html={body}
                    />
                  )}
                </div>

                <div className="NodeVacancyPage__hero__meta">
                  {locations.length > 0 && (
                    <span className="NodeVacancyPage__hero__meta__data">
                      {locations.map((location) => (
                        <>
                          <Icon name="location_on" flair="outlined" />{location.entity.name[0].value}
                        </>
                      ))}
                    </span>
                  )}
                  <span className="NodeVacancyPage__hero__meta__data">
                    <Icon name="euro_symbol" flair="outlined" />
                    <span>
                      {(salaryMinimum?.number && salaryMinimum.number < salaryMaximum.number) && (
                        <>{Number(salaryMinimum.number)} - </>
                      )}
                      {Number(salaryMaximum.number)}
                      &nbsp;{t(`content_types.vacancy.${salaryType}`)}
                    </span>
                  </span>
                  <span className="NodeVacancyPage__hero__meta__data">
                    <Icon name="schedule" flair="outlined" />
                    <div>
                      {translatedEmploymentTypes.map((type, i) => (
                        <span>
                          {type}
                          {i < translatedEmploymentTypes.length - 2 && ', '}
                          {i === translatedEmploymentTypes.length - 2 && (lang === DEFAULT_LANGUAGE ? ' and ' : ' en ')}
                        </span>
                      ))}
                    </div>
                  </span>
                </div>

                <div className="NodeVacancyPage__hero__applybutton">
                  <ButtonWrapper
                    // eslint-disable-next-line max-len
                    href={`mailto:${vacancyEmailAddress}?subject=${vacancyEmailSubject}%20${htmlTitle}&body=${vacancyEmailBody}`}
                    iconName="mail"
                  >
                    <span>{t('content_types.vacancy.apply')}</span>
                  </ButtonWrapper>
                </div>
              </div>
            </div>

            {image && (
              <div className="col-12">
                <div className="NodeVacancyPage__hero__image">
                  <DynamicMediaItem
                    media={image}
                    imageAspectRatio="16_9"
                  />
                </div>
              </div>
            )}
          </div>
        </Container>

        <div className="NodeVacancyPage__points__outer-wrapper">
          <Container>
            <div className="row NodeVacancyPage__points__inner-wrapper">
              <div className="col-12 col-md-10 offset-md-1 col-lg-5 offset-lg-1">
                <div className="NodeVacancyPage__points">
                  <h3 className="NodeVacancyPage__points__title">
                    {t('content_types.vacancy.what_we_offer')}
                  </h3>
                  <div className="NodeVacancyPage__points__list">
                    {benefits.map((benefit) => (
                      <span className="NodeVacancyPage__points__point">
                        {benefit.entity.name[0].value}
                      </span>
                    ))}
                  </div>
                </div>

              </div>
              <div className="col-12 col-md-10 offset-md-1 col-lg-5 offset-lg-0">
                <div className="NodeVacancyPage__points">
                  <h3 className="NodeVacancyPage__points__title">
                    {t('content_types.vacancy.what_we_search_for')}
                  </h3>
                  <div className="NodeVacancyPage__points__list">
                    {requirements.map((requirement) => (
                      <span className="NodeVacancyPage__points__point">
                        {requirement.entity.name[0].value}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>

        <ParagraphsRenderer className="NodeVacancyPage__paragraphs" paragraphs={paragraphs} />
      </article>
    </>
  );
};

export default NodeVacancyPage;
